/**
 * AddonCptPicker
 * =====================================================================
 * Multi-select picker for add-on CPT codes filtered by the chosen
 * primary CPT.  Used in both the Appointment Dialog and the Note
 * header so the provider has ONE familiar control everywhere.
 *
 * Why a dedicated component (Feb 2026):
 *   The previous appointment-dialog implementation was a single Select
 *   (one add-on max) and the note header had nothing.  Users (nurses
 *   in particular) need to stack multiple add-ons per encounter
 *   (e.g. 99213 + 90833 + 90785), and the Note header needs the same
 *   control because providers often edit the CPT after scheduling.
 *
 * Behavior:
 *   • Lists add-on CPTs whose ``requires_primary_codes`` includes the
 *     selected primary CPT id.
 *   • Renders selected add-ons as removable chips, with a "+ Add an
 *     add-on" dropdown to append more.
 *   • Empty state: shows nothing when no compatible add-ons exist
 *     (the appointment dialog flow already used this convention).
 *   • Read-only / disabled mode for view-only contexts.
 *
 * Props:
 *   primaryCptId       — id of the primary CPT (used to filter)
 *   selectedIds        — array of add-on CPT doc ids currently chosen
 *   onChange(ids)      — called with the new array on every add/remove
 *   cptCodes           — full list of CPT docs to draw from
 *   disabled           — read-only
 *   testIdPrefix       — prefix for data-testid attributes
 *
 * Note: this is a controlled component — caller owns state.
 */
import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';

export const AddonCptPicker = ({
  primaryCptId,
  selectedIds = [],
  onChange,
  cptCodes = [],
  disabled = false,
  testIdPrefix = 'addon-cpt',
  label = 'Add-on CPT Codes (Optional)',
  helpText = 'Add-ons are billed as separate service lines with modifier 25 on the primary code. Multiple add-ons can be stacked.',
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  // Compatible add-ons: active, flagged as add-on, AND list the
  // primary CPT in requires_primary_codes.  Excludes any already
  // selected so the picker only offers fresh options.
  const compatibleAddons = useMemo(() => {
    if (!primaryCptId) return [];
    return (cptCodes || [])
      .filter((c) =>
        c.is_addon_code &&
        c.is_active &&
        Array.isArray(c.requires_primary_codes) &&
        c.requires_primary_codes.includes(primaryCptId) &&
        !selectedIds.includes(c.id)
      )
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }, [cptCodes, primaryCptId, selectedIds]);

  // Resolve selected ids to their full doc for chip display.
  const selectedDocs = useMemo(() => {
    const byId = {};
    for (const c of cptCodes || []) {
      if (c && c.id) byId[c.id] = c;
    }
    return (selectedIds || [])
      .map((id) => byId[id])
      .filter(Boolean);
  }, [cptCodes, selectedIds]);

  // Hide entirely when there are NO add-ons currently selected AND
  // no compatible add-ons available — matches the legacy UX so we
  // don't add visual clutter to most appointments.
  if (!primaryCptId) return null;
  if (selectedDocs.length === 0 && compatibleAddons.length === 0) return null;

  const handleAdd = (id) => {
    if (!id || id === 'none') return;
    if (selectedIds.includes(id)) return;
    onChange?.([...selectedIds, id]);
    setPickerOpen(false);
  };

  const handleRemove = (id) => {
    onChange?.(selectedIds.filter((x) => x !== id));
  };

  return (
    <div data-testid={`${testIdPrefix}-container`}>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-1 items-center">
        {selectedDocs.map((doc) => (
          <span
            key={doc.id}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-mono"
            data-testid={`${testIdPrefix}-chip-${doc.code}`}
          >
            <span className="font-semibold">{doc.code}</span>
            {doc.description ? (
              <span className="text-teal-600 truncate max-w-[180px]">
                {doc.description}
              </span>
            ) : null}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(doc.id)}
                className="text-teal-700 hover:text-teal-900 hover:bg-teal-100 rounded-full p-0.5"
                aria-label={`Remove ${doc.code}`}
                data-testid={`${testIdPrefix}-remove-${doc.code}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}

        {!disabled && compatibleAddons.length > 0 && (
          <Select
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            value=""
            onValueChange={handleAdd}
          >
            <SelectTrigger
              className="w-auto h-7 px-2 py-0 text-xs border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
              data-testid={`${testIdPrefix}-add-btn`}
            >
              <SelectValue placeholder={(
                <span className="flex items-center gap-1">
                  <Plus className="h-3 w-3" />
                  Add an add-on
                </span>
              )} />
            </SelectTrigger>
            <SelectContent className="z-[10002]">
              {compatibleAddons.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id}
                  data-testid={`${testIdPrefix}-option-${c.code}`}
                >
                  <span className="font-mono font-semibold mr-2">{c.code}</span>
                  <span className="text-gray-600">{c.description}</span>
                  {c.base_price ? (
                    <span className="text-gray-400 ml-2">${c.base_price}</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  );
};

export default AddonCptPicker;
